import QRCode from "qrcode";
import JsBarcode from "jsbarcode";

/** Caption burned into every downloaded QR PNG (print / share). */
export const QR_DOWNLOAD_CAPTION_RU = "Сканировать через приложение MVK One";
export const QR_DOWNLOAD_CAPTION_EN = "Scan with the MVK One app";

export function slugifyFilename(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9а-яё]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48) || "code";
}

export async function generateQrDataUrl(text: string, size = 256): Promise<string> {
  return QRCode.toDataURL(text, {
    width: size,
    margin: 2,
    errorCorrectionLevel: "M",
  });
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("qr_image_load_failed"));
    img.src = dataUrl;
  });
}

/** Wrap caption into ≤2 lines that fit the canvas width. */
function wrapCaption(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  if (ctx.measureText(text).width <= maxWidth) return [text];

  const lines: string[] = [];
  let current = words[0]!;
  for (let i = 1; i < words.length; i++) {
    const next = `${current} ${words[i]}`;
    if (ctx.measureText(next).width <= maxWidth) {
      current = next;
    } else {
      lines.push(current);
      current = words[i]!;
      if (lines.length >= 1) {
        // remainder on second line (may be slightly tight — shrink font at call site if needed)
        lines.push([current, ...words.slice(i + 1)].join(" "));
        return lines;
      }
    }
  }
  lines.push(current);
  return lines;
}

/**
 * QR PNG with white padding and a caption under the code.
 * Used by all admin / exhibitor / visitor download actions.
 */
export async function generateQrPngWithCaption(
  value: string,
  options?: { size?: number; caption?: string | null },
): Promise<string> {
  const size = options?.size ?? 512;
  const caption =
    options?.caption === null
      ? null
      : (options?.caption ?? QR_DOWNLOAD_CAPTION_RU).trim() || null;

  const qrDataUrl = await generateQrDataUrl(value, size);
  if (!caption) return qrDataUrl;

  const img = await loadImage(qrDataUrl);
  const padX = Math.round(size * 0.06);
  const padTop = Math.round(size * 0.04);
  const gap = Math.round(size * 0.04);
  const padBottom = Math.round(size * 0.06);

  let fontSize = Math.max(18, Math.round(size * 0.048));
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return qrDataUrl;

  const maxTextWidth = size; // match QR width; canvas has side padding around both
  ctx.font = `600 ${fontSize}px system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`;
  let lines = wrapCaption(ctx, caption, maxTextWidth);
  // If second line still overflows, shrink font once.
  if (lines.some((l) => ctx.measureText(l).width > maxTextWidth)) {
    fontSize = Math.max(14, Math.round(fontSize * 0.88));
    ctx.font = `600 ${fontSize}px system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`;
    lines = wrapCaption(ctx, caption, maxTextWidth);
  }

  const lineHeight = Math.round(fontSize * 1.35);
  const textBlock = lines.length * lineHeight;
  canvas.width = size + padX * 2;
  canvas.height = padTop + size + gap + textBlock + padBottom;

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, padX, padTop, size, size);

  ctx.fillStyle = "#111827";
  ctx.font = `600 ${fontSize}px system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  const cx = canvas.width / 2;
  let y = padTop + size + gap;
  for (const line of lines) {
    ctx.fillText(line, cx, y);
    y += lineHeight;
  }

  return canvas.toDataURL("image/png");
}

export function generateBarcodeDataUrl(text: string, height = 80): string {
  const canvas = document.createElement("canvas");
  JsBarcode(canvas, text, {
    format: "CODE128",
    displayValue: true,
    margin: 12,
    height,
    fontSize: 14,
  });
  return canvas.toDataURL("image/png");
}

export function downloadDataUrl(dataUrl: string, filename: string): void {
  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = filename;
  link.click();
}

export async function downloadQrPng(
  value: string,
  baseName: string,
  options?: { caption?: string | null },
): Promise<void> {
  const text = value?.trim();
  if (!text) throw new Error("empty_qr_value");
  const dataUrl = await generateQrPngWithCaption(text, {
    size: 512,
    caption: options?.caption,
  });
  downloadDataUrl(dataUrl, `${slugifyFilename(baseName)}-qr.png`);
}

export function downloadBarcodePng(value: string, baseName: string): void {
  const text = value?.trim();
  if (!text) throw new Error("empty_barcode_value");
  const dataUrl = generateBarcodeDataUrl(text);
  downloadDataUrl(dataUrl, `${slugifyFilename(baseName)}-barcode.png`);
}

export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
